import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  IEntityUser,
  IUser,
  IUserAccount,
  IUserEntity,
} from '@boma/client-shared';
import { Store } from '@ngrx/store';
import { Observable, of, Subscription } from 'rxjs';
import {
  createUserAccount,
  deleteUser,
  getUserAccount,
  loadUsers,
  UsersFacade,
} from '@boma/supplier-portal/users';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { BreadcrumbService } from '@boma/ui';
import { getUser } from '@boma/supplier-portal/shared/state';

@Component({
  selector: 'boma-index',
  templateUrl: './index.component.html',
  styleUrls: ['./index.component.scss'],
})
export class IndexComponent implements OnInit, OnDestroy {
  columns = [
    {
      field: 'user',
      header: 'Name',
      converter: (val: IUserAccount) => val.firstName,
    },
    {
      field: 'user',
      header: 'Surname',
      converter: (val: IUserAccount) => val.surname,
    },
    {
      field: 'identificationNumber',
      header: 'ID Number',
    },
    {
      field: 'employeeNumber',
      header: 'Employee Number',
    },
    {
      field: 'position',
      header: 'Position',
    },
    {
      field: 'companyContactNumber',
      header: 'Company Contact Number',
    },
    {
      field: 'personalContactNumber',
      header: 'Personal Contact Number',
    },
    // TODO: Uncomment after number is exposed on backend endpoint.
    // Verify field name 'bomaContactNumber'
    {
      field: 'user',
      header: 'Boma Number',
      converter: (val: IUserAccount) => val.bomaContactNumber,
    },
    {
      field: 'department',
      header: 'Department',
    },
  ];

  loading$ = this.usersFacade.loaded$;
  user$: Observable<IUserEntity>; // The currently logged in user - we need to extract the supplier entity from this user
  users$: Observable<IEntityUser[]>; // the result list of users
  paginationData$: Observable<any>;
  createUserIdentified$: Observable<IUserAccount> = of(null); // the account a new user will be bound to, this only becomes populated if an account is discovered via a phone number
  supplierId$: string = null;

  modalProcessing$: Observable<boolean> = of(false);
  showAddModal = true;

  currentPage = 1;
  perPage = 25;

  searchAccountFormGroup: FormGroup = new FormGroup({
    emailAddressOrPhoneNumber: new FormControl('', Validators.required),
  });

  searchedUser: {
    firstName: string;
    surname: string;
    email: string;
    number: string;
  };

  userSubscription: Subscription;

  createFormGroup: FormGroup = new FormGroup({
    identificationNumber: new FormControl('', Validators.required),
    employeeNumber: new FormControl('', Validators.required),
    position: new FormControl('', Validators.required),
    companyContactNumber: new FormControl('', Validators.required),
    personalContactNumber: new FormControl('', Validators.required),
    department: new FormControl('', Validators.required),
    notes: new FormControl('', Validators.required),
  });

  query = '';

  currentUser: IUser;

  constructor(
    private store: Store,
    private router: Router,
    private usersFacade: UsersFacade,
    private messageService: MessageService,
    private breadcrumbService: BreadcrumbService,
    private activatedRoute: ActivatedRoute
  ) {
    this.breadcrumbService.setItems([{ label: 'Users' }]);
  }

  ngOnInit(): void {
    // Get the currently logged in user
    this.user$ = this.store.select(getUser); // get the current user
    this.userSubscription = this.user$.subscribe((x) => (this.currentUser = x));
    this.usersFacade.dispatch(
      loadUsers({
        query: this.query,
        page: this.currentPage,
        perPage: this.perPage,
        archived: false,
      })
    ); // get a list of user entities

    // Get a list of all the users
    this.users$ = this.usersFacade.allUsers$;
    this.paginationData$ = this.usersFacade.paginatedData$;
  }

  handleChangePageClicked(ev = null) {
    if (ev === this.currentPage) return;
    this.currentPage = ev;
    this.usersFacade.dispatch(
      loadUsers({
        query: this.query,
        page: ev,
        perPage: this.perPage,
        archived: false,
      })
    );
  }

  search(query) {
    this.query = query;
    this.usersFacade.dispatch(
      loadUsers({
        query: this.query,
        page: 1,
        perPage: this.perPage,
        archived: false,
      })
    );
  }

  // View a user
  onView(data) {
    let entityId = '';
    this.user$.subscribe((x) => (entityId = x.selectedOrganizationId));
    const url = this.router
      .createUrlTree(['/users/details', entityId, data.user.id])
      .toString();
    window.open(url, '_blank');
  }

  // Search for an existing account
  onAccountSearch(e) {
    this.modalProcessing$ = of(true);

    // Get the input phone number and lookup the account
    const search =
      this.searchAccountFormGroup.controls.emailAddressOrPhoneNumber.value;

    this.usersFacade.dispatch(
      getUserAccount({ emailAddressOrPhoneNumber: search })
    );

    // If an error occurs, we need to display it to the client
    this.usersFacade.error$.subscribe((err) => {
      if (err !== null) {
        this.messageService.clear();
        this.messageService.add({
          severity: 'error',
          summary: 'Unable to Locate Account',
          detail:
            'No account is listed for ' +
            search +
            ', the user will have to create a Boma account in order for you to link/add them.',
        });
        this.createFormGroup.patchValue({
          companyContactNumber: '',
          personalContactNumber: '',
        });
      } else {
        if (search.indexOf('@') === -1) {
          this.createFormGroup.patchValue({
            companyContactNumber: search,
            personalContactNumber: search,
          });
        }
      }
      this.searchAccountFormGroup.reset();
    });

    // If successful, then we return the user account
    this.usersFacade.createUserIdentified$.subscribe((s) => {
      this.createUserIdentified$ = this.usersFacade.createUserIdentified$;
      this.createUserIdentified$.subscribe(
        (x) => (this.modalProcessing$ = of(false))
      ); // stop loading spinner once account is fetched
    });
  }

  inviteUser() {
    alert('Under development');
  }

  handleUserDelete(user) {
    this.usersFacade.dispatch(deleteUser({ userId: user.id }));
  }

  createItem() {
    this.modalProcessing$ = of(true);
    this.usersFacade.createUserIdentified$.subscribe((x) => {
      this.usersFacade.dispatch(
        createUserAccount({
          user: { userId: x.id, ...this.createFormGroup.value },
        })
      );

      this.showAddModal = false;
    });
  }

  ngOnDestroy() {
    this.userSubscription?.unsubscribe();
  }
}
